import React, { useMemo, useState } from "react";
import styles from "./therapeutic.module.css";
import { Button, Section, SectionIntro } from "../../../../COMPONENTS/UI";
import { stripTags, therapeuticGroups } from "../../../../CONTENT/therapeutic";
import { investigators } from "../../../../CONSTANTS/investigatorsList";

const asList = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const tokens = (area) =>
  area
    .toLowerCase()
    .split(/[/,]/)
    .map((t) => t.trim())
    .filter((t) => t.length > 3)
    .map((t) => t.slice(0, 6));

const matchInvestigators = (area) => {
  const keys = tokens(area);
  if (!keys.length) return [];
  return investigators.filter((inv) => {
    const fields = [...asList(inv.TheraupaticalArea), ...asList(inv.specialization)].map(
      (f) => f.toLowerCase()
    );
    return keys.some((k) => fields.some((f) => f.includes(k)));
  });
};

const Therapeutic = ({
  id = "therapeutic-areas",
  eyebrow = "Therapeutic expertise",
  title = "Broad expertise, from chronic care to new vaccines.",
  lead = "Select a group and an area to see how KUR's investigators and locations line up.",
  showFullLink = true,
}) => {
  const [groupId, setGroupId] = useState(therapeuticGroups[0].id);
  const group = therapeuticGroups.find((g) => g.id === groupId);
  const items = useMemo(() => group.items.map(stripTags), [group]);
  const isList = group.id !== "sample-diagnostic";

  const [selected, setSelected] = useState({
    [therapeuticGroups[0].id]: "Family Practice",
  });
  const area = isList ? selected[group.id] || items[0] : null;
  const matches = useMemo(() => (area ? matchInvestigators(area) : []), [area]);
  const places = useMemo(() => {
    const seen = new Map();
    matches.forEach((m) => {
      const key = `${m.location?.city}, ${m.location?.state}`;
      if (m.location?.city) seen.set(key, true);
    });
    return [...seen.keys()];
  }, [matches]);

  return (
    <Section tone="ink" id={id} labelledBy={`${id}-title`}>
      <SectionIntro id={`${id}-title`} eyebrow={eyebrow} title={title} lead={lead} />

      <div className={styles.groups} role="tablist" aria-label="Therapeutic groups">
        {therapeuticGroups.map((g) => (
          <button
            key={g.id}
            type="button"
            role="tab"
            aria-selected={g.id === groupId}
            aria-controls="therapeutic-panel"
            className={styles.groupTab}
            onClick={() => setGroupId(g.id)}
          >
            {g.title}
          </button>
        ))}
      </div>

      <div className={styles.layout} id="therapeutic-panel" role="tabpanel">
        <div>
          <p className={styles.groupDesc}>{group.description}</p>
          {isList ? (
            <ul className={styles.chips}>
              {items.map((it) => (
                <li key={it}>
                  <button
                    type="button"
                    aria-pressed={it === area}
                    className={styles.chip}
                    onClick={() => setSelected((s) => ({ ...s, [group.id]: it }))}
                  >
                    {it}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <ul className={styles.plain}>
              {group.items.map((it) => (
                <li key={it} dangerouslySetInnerHTML={{ __html: it }} />
              ))}
            </ul>
          )}
        </div>

        <aside className={styles.detail} aria-live="polite">
          {isList ? (
            <>
              <p className={styles.detailLabel}>{group.title}</p>
              <h3 className={styles.detailTitle}>{area}</h3>
              {matches.length > 0 ? (
                <>
                  <p className={styles.detailText}>
                    {matches.length} KUR investigator{matches.length > 1 ? "s" : ""} in this
                    area.
                  </p>
                  <ul className={styles.people}>
                    {matches.slice(0, 6).map((m) => (
                      <li key={m.name}>{m.name}</li>
                    ))}
                    {matches.length > 6 && <li>+{matches.length - 6} more</li>}
                  </ul>
                  {places.length > 0 && (
                    <p className={styles.places}>{places.join(" · ")}</p>
                  )}
                </>
              ) : (
                <p className={styles.detailText}>
                  Part of KUR's therapeutic portfolio. Talk to us about protocols in
                  this area.
                </p>
              )}
            </>
          ) : (
            <>
              <p className={styles.detailLabel}>{group.title}</p>
              <h3 className={styles.detailTitle}>{group.description}</h3>
              <p className={styles.detailText}>{group.note}</p>
            </>
          )}
          {showFullLink && (
            <Button href="/sponsors#therapeutic" variant="link" tone="dark">
              Full therapeutic list
            </Button>
          )}
        </aside>
      </div>
    </Section>
  );
};

export default Therapeutic;
